/**
 * What he can say back, and when each thing is worth saying.
 *
 * The first version offered the same two chips under every line she ever
 * spoke, which made talking back a button rather than an answer: „Ir ką man
 * daryt?" under a logged cucumber is not a question anybody has. A chip is
 * only interesting if it is the thing a person would actually have said at
 * that exact moment — so eligibility is the whole design here, and the pools
 * in `catalog/interaction.ts` can be specific because the chip that draws
 * them only ever appears where it fits.
 *
 * Twelve of them, in four groups:
 *
 * - **always** — the three anyone might tap at any line (advice, a shush, a
 *   defensive "I know"), plus the two that turn the conversation round on her
 *   (asking how she is, asking after the cat). Those two are the closest this
 *   app gets to somebody being kind to a ghost, so they are rare by weight
 *   rather than by rule.
 * - **over budget** — the three that only exist when a number went the wrong
 *   way: no regrets, how bad is it, and tomorrow I'll fix it.
 * - **the hour and the mood** — 3 a.m. gets „Neužmiegu", a return after a
 *   silence gets „Grįžau".
 * - **the repair** — „Atsiprašau", offered only while she is cooled, and the
 *   only chip that changes her state rather than just earning a line.
 */

import type { SassContext } from '@gramas/shared'

export interface ChipDef {
  /** Travels to the engine as `ctx.surface`; keys `chip_comeback.<id>`. */
  id: string
  /** His words, as they appear on the button. Short — two chips share a row. */
  label: string
  /** Relative frequency among the chips that fit. */
  weight: number
  fits: (ctx: SassContext) => boolean
}

const anytime = () => true
const isOver = (ctx: SassContext) =>
  ctx.isOver === true || (ctx.remainingKcal != null && ctx.remainingKcal < 0)
const isBig = (ctx: SassContext) => (ctx.entryKcal ?? 0) >= 600
const isNight = (ctx: SassContext) => ctx.hour >= 0 && ctx.hour < 4

/**
 * Every chip, with the situation that earns it. Order is only for reading;
 * selection is weighted.
 */
export const CHIPS: readonly ChipDef[] = [
  // ── The three that always fit ──
  { id: 'daryt', label: 'Ir ką man daryt?', weight: 10, fits: anytime },
  { id: 'nekomentuok', label: 'Nekomentuok', weight: 10, fits: anytime },
  { id: 'zinau', label: 'Pats žinau', weight: 9, fits: anytime },

  // ── Turning it round on her. Rare, and the warmest thing here. ──
  { id: 'otu', label: 'O tu kaip?', weight: 5, fits: anytime },
  { id: 'kate', label: 'Kaip Katė?', weight: 4, fits: anytime },
  { id: 'aciu', label: 'Ačiū', weight: 3, fits: anytime },

  // ── When a number went the wrong way ──
  { id: 'verta', label: 'Verta buvo', weight: 9, fits: (ctx) => isOver(ctx) || isBig(ctx) },
  { id: 'blogai', label: 'Labai blogai?', weight: 9, fits: (ctx) => isOver(ctx) || isBig(ctx) },
  { id: 'rytoj', label: 'Rytoj pasitaisysiu', weight: 7, fits: isOver },

  // ── The hour, and the way back ──
  { id: 'nemiegu', label: 'Neužmiegu', weight: 8, fits: isNight },
  {
    id: 'grizau',
    label: 'Grįžau',
    weight: 10,
    fits: (ctx) => ctx.mood === 'neglected' || (ctx.daysSinceLastLog ?? 0) >= 3,
  },

  // ── The repair. Offered only while she is cooled; see `chipsFor`. ──
  { id: 'atsiprasau', label: 'Atsiprašau', weight: 1, fits: () => false },
]

const byId = new Map(CHIPS.map((chip) => [chip.id, chip]))

export function chipById(id: string): ChipDef | null {
  return byId.get(id) ?? null
}

/** Weighted pick without replacement. */
function draw(pool: ChipDef[], count: number, rng: () => number): ChipDef[] {
  const taken: ChipDef[] = []
  const rest = [...pool]
  while (taken.length < count && rest.length > 0) {
    const total = rest.reduce((sum, chip) => sum + chip.weight, 0)
    let roll = rng() * total
    let index = rest.length - 1
    for (let i = 0; i < rest.length; i++) {
      roll -= rest[i]!.weight
      if (roll <= 0) {
        index = i
        break
      }
    }
    taken.push(rest[index]!)
    rest.splice(index, 1)
  }
  return taken
}

/**
 * The chips to offer under this line: two that fit, or — while she is cooled
 * — the single one that can do something about it.
 *
 * The apology stands alone deliberately. Banter next to it would make it one
 * option among several rather than the thing on the table, and a cooled day
 * is not a day she is making small talk on.
 */
export function chipsFor(ctx: SassContext, cooled: boolean, rng: () => number): ChipDef[] {
  if (cooled) {
    const repair = byId.get('atsiprasau')
    return repair == null ? [] : [repair]
  }
  return draw(
    CHIPS.filter((chip) => chip.fits(ctx)),
    2,
    rng,
  )
}

/**
 * The chips a comeback may itself carry, so an exchange can run two rounds
 * before she is done with it.
 *
 * Only the turn-it-round three, and never the one just used: a follow-up is
 * somebody staying in the conversation, and „Nekomentuok" twice is somebody
 * pressing a button. The second comeback never offers anything further —
 * two rounds is a moment, three is a chat, and the chat is a different
 * feature with a different door.
 */
export function followUpChip(usedId: string, rng: () => number): ChipDef | null {
  const pool = CHIPS.filter(
    (chip) => chip.id !== usedId && ['otu', 'kate', 'aciu', 'nekomentuok'].includes(chip.id),
  )
  return draw(pool, 1, rng)[0] ?? null
}
